"use client";

import { useMemo, useState } from "react";
import {
  Checkbox,
  CopyButton,
  Notice,
  Panel,
  SelectField,
  StatCard,
  StatGrid,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  HINT,
  LABEL,
  RESULT_CARD,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import { BibReport } from "@/components/tools/reference-tools/bib-report";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { downloadText } from "@/lib/tool-kit/download";
import {
  type CapsProtection,
  type CleanOptions,
  cleanBibtex,
  DEFAULT_CLEAN_OPTIONS,
  type FieldOrder,
  type IndentStyle,
  type SortOrder,
} from "@/lib/tool-kit/reference-tools/bibtex-clean";

/** English strings. A translated content file overrides these through `ui`. */
export const BIBTEX_CLEANER_STRINGS = {
  inputLabel: "Your .bib file — paste it here or load it from your computer",
  inputPlaceholder:
    '@ARTICLE{hirsch2005,\n  Author = {Hirsch, J. E.},\n  title = "An index to quantify an individual\'s scientific research output",\n  journal = {Proceedings of the National Academy of Sciences},\n  year = 2005, month = {November},\n  pages = {16569-16572},\n  note = {}\n}',
  loadFile: "Load a .bib file",
  loadFailed: "That file could not be read as text.",
  optionsHeading: "Clean-up options",
  lowercase: "Lower-case entry types and field names (@ARTICLE → @article)",
  trimWhitespace: "Collapse extra spaces and line breaks inside values",
  braces: "Write values in braces instead of quotation marks",
  pageDashes: "Write page ranges with a double hyphen (1--10)",
  monthMacros: "Write months as BibTeX's macros (jan … dec)",
  removeEmpty: "Remove empty fields",
  bareDoi: "Store DOIs without the https://doi.org/ prefix",
  capsLabel: "Protect capitals in titles",
  capsNone: "Leave titles as they are",
  capsAcronyms: "Acronyms and mixed case: {DNA}, {mRNA}, {iPhone}",
  capsAll: "Every capitalised word after the first",
  orderLabel: "Field order",
  orderStandard: "Standard: author, title, journal, volume, pages, year…",
  orderKeep: "Keep each entry's own order",
  orderAlphabetical: "Alphabetical",
  indentLabel: "Indentation",
  indentTwo: "Two spaces",
  indentFour: "Four spaces",
  indentTab: "Tab",
  sortLabel: "Sort entries",
  sortNone: "Keep file order",
  sortKey: "By citation key",
  sortAuthor: "By first author, then year",
  sortYearDesc: "By year, newest first",
  sortYearAsc: "By year, oldest first",
  emptyPrompt:
    "Paste a .bib file or load one. It is read in your browser — nothing is uploaded.",
  noEntries:
    "No BibTeX entries found. An entry starts with @ and its type, such as @article{key, …}.",
  results: "Cleaned file",
  statEntries: "Entries",
  statErrors: "Errors",
  statWarnings: "Warnings",
  statChanges: "Changes",
  copy: "Copy .bib",
  copied: "Copied!",
  download: "Download cleaned.bib",
  rewrittenNote:
    "Every entry is rewritten with one field per line and the = signs aligned. Macros, # joins, comments and unreadable entries are kept as they were.",
  sortedNote:
    "Entries sorted; @string definitions, @preamble and comments stay at the top.",
  issuesHeading: "Problems found",
  noIssues:
    "No problems found: every entry has its required fields, and no key or DOI repeats.",
  changesHeading: "Every change made",
  noChanges: "Nothing needed changing with these options.",
  changeCount: "one:{count} change|other:{count} changes",
  line: "Line {line}",
  error: "Error",
  warning: "Warning",
  outsideEntries: "Outside an entry",
  issues: {
    parse:
      "The reader could not finish this entry: {detail}. It is kept exactly as written.",
    missingKey: "An @{detail} entry has no citation key.",
    invalidKey:
      "The key contains a space or a character BibTeX cannot read in a key.",
    duplicateKey:
      "This key is already used by an earlier entry — BibTeX keeps only the first.",
    duplicateKeyCase:
      "This key differs from {other} only in capitals — BibTeX warns about it; biber treats them as two keys.",
    missingField: "Missing a required field: {detail}.",
    biblatexType:
      "@{detail} is a biblatex type. biblatex formats it; classic BibTeX styles fall back to @misc with a warning.",
    unknownType:
      "@{detail} is not a standard BibTeX or biblatex type, so styles treat it as @misc.",
    duplicateField:
      "“{detail}” appears twice — BibTeX keeps the first and ignores the other.",
    undefinedMacro:
      "{detail} is used as a macro but never defined with @string — BibTeX prints nothing for it.",
    sameDoi: "Same DOI as {other} — probably the same work entered twice.",
    sameTitle: "Same title as {other} — check whether it is the same work.",
    badYear:
      "Year “{detail}” is not a four-digit year — BibTeX sorts and labels by it.",
  },
  parseErrors: {
    missingDelimiter: "no opening brace after the entry type",
    missingKey: "no citation key",
    expectedFieldName: "a field name was expected",
    expectedEquals: "an = sign was expected after the field name",
    expectedValue: "a value was expected after =",
    expectedCommaOrClose:
      "a comma or closing brace was expected — usually a missing comma between two fields",
    unbalancedBraces: "the braces do not balance",
    unterminatedQuote: "a quoted value is never closed",
    unterminatedEntry: "the entry is never closed",
  },
  changes: {
    type: "Entry type {before} → {after}",
    fieldName: "Field name {before} → {after}",
    whitespace: "{field}: extra spaces and line breaks removed",
    pages: "{field}: {before} → {after}",
    month: "{field}: {before} → {after}",
    empty: "{field}: empty field removed",
    caps: "{field}: {before} → {after}",
    braces: "{field}: quotation marks replaced by braces",
    doi: "{field}: {before} → {after}",
    order: "Fields put in the chosen order",
  },
} as const;

export type BibtexCleanerStrings = typeof BIBTEX_CLEANER_STRINGS;

const BIB_MIME = "application/x-bibtex;charset=utf-8";

type Toggle =
  | "lowercase"
  | "trimWhitespace"
  | "braces"
  | "pageDashes"
  | "monthMacros"
  | "removeEmpty"
  | "bareDoi";

const TOGGLES: readonly Toggle[] = [
  "lowercase",
  "trimWhitespace",
  "braces",
  "pageDashes",
  "monthMacros",
  "removeEmpty",
  "bareDoi",
];

function OptionsPanel({
  options,
  onChange,
  strings,
}: {
  options: CleanOptions;
  onChange: (next: CleanOptions) => void;
  strings: BibtexCleanerStrings;
}) {
  const set = <K extends keyof CleanOptions>(key: K, value: CleanOptions[K]) =>
    onChange({ ...options, [key]: value });
  return (
    <Panel title={strings.optionsHeading}>
      <div className="grid gap-2 sm:grid-cols-2">
        {TOGGLES.map((key) => (
          <Checkbox
            checked={options[key]}
            key={key}
            label={strings[key]}
            onChange={(checked) => set(key, checked)}
          />
        ))}
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField<CapsProtection>
          id="bc-caps"
          label={strings.capsLabel}
          onChange={(value) => set("protectCaps", value)}
          options={[
            { value: "none", label: strings.capsNone },
            { value: "acronyms", label: strings.capsAcronyms },
            { value: "all", label: strings.capsAll },
          ]}
          value={options.protectCaps}
        />
        <SelectField<FieldOrder>
          id="bc-order"
          label={strings.orderLabel}
          onChange={(value) => set("fieldOrder", value)}
          options={[
            { value: "standard", label: strings.orderStandard },
            { value: "keep", label: strings.orderKeep },
            { value: "alphabetical", label: strings.orderAlphabetical },
          ]}
          value={options.fieldOrder}
        />
        <SelectField<IndentStyle>
          id="bc-indent"
          label={strings.indentLabel}
          onChange={(value) => set("indent", value)}
          options={[
            { value: "two", label: strings.indentTwo },
            { value: "four", label: strings.indentFour },
            { value: "tab", label: strings.indentTab },
          ]}
          value={options.indent}
        />
        <SelectField<SortOrder>
          id="bc-sort"
          label={strings.sortLabel}
          onChange={(value) => set("sort", value)}
          options={[
            { value: "none", label: strings.sortNone },
            { value: "key", label: strings.sortKey },
            { value: "author", label: strings.sortAuthor },
            { value: "year-desc", label: strings.sortYearDesc },
            { value: "year-asc", label: strings.sortYearAsc },
          ]}
          value={options.sort}
        />
      </div>
    </Panel>
  );
}

export default function BibtexCleanerWidget() {
  const strings = useToolUi(BIBTEX_CLEANER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [text, setText] = useState("");
  const [options, setOptions] = useState<CleanOptions>(DEFAULT_CLEAN_OPTIONS);
  const [loadFailed, setLoadFailed] = useState(false);

  const result = useMemo(
    () => (text.trim() === "" ? null : cleanBibtex(text, options)),
    [text, options]
  );
  const errors =
    result?.issues.filter((issue) => issue.severity === "error").length ?? 0;
  const warnings = (result?.issues.length ?? 0) - errors;

  async function handleFile(file: File | undefined) {
    if (!file) {
      return;
    }
    try {
      setText(await file.text());
      setLoadFailed(false);
    } catch {
      setLoadFailed(true);
    }
  }

  return (
    <section className="space-y-6">
      <TextAreaField
        id="bc-input"
        label={strings.inputLabel}
        onChange={setText}
        placeholder={strings.inputPlaceholder}
        rows={10}
        value={text}
      />
      <div className="space-y-2">
        <label className={LABEL} htmlFor="bc-file">
          {strings.loadFile}
        </label>
        <input
          accept=".bib,.bibtex,.txt,text/plain"
          className="block w-full text-foreground text-sm file:mr-3 file:rounded-md file:border file:border-border file:bg-card file:px-3 file:py-1 file:text-foreground"
          id="bc-file"
          onChange={(event) => handleFile(event.target.files?.[0])}
          type="file"
        />
        {loadFailed ? <p className={HINT}>{strings.loadFailed}</p> : null}
      </div>
      <OptionsPanel onChange={setOptions} options={options} strings={strings} />
      <div aria-live="polite" className="space-y-6">
        {result === null ? <Notice>{strings.emptyPrompt}</Notice> : null}
        {result && result.entries === 0 ? (
          <Notice>{strings.noEntries}</Notice>
        ) : null}
        {result && result.entries > 0 ? (
          <div className={`${RESULT_CARD} space-y-4`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className={WIDGET_H2}>{strings.results}</h2>
              <div className="flex flex-wrap gap-3">
                <CopyButton
                  copiedLabel={strings.copied}
                  label={strings.copy}
                  text={result.output}
                />
                <button
                  className={BUTTON_SECONDARY}
                  onClick={() =>
                    downloadText("cleaned.bib", result.output, BIB_MIME)
                  }
                  type="button"
                >
                  {strings.download}
                </button>
              </div>
            </div>
            <StatGrid>
              <StatCard
                emphasis
                label={strings.statEntries}
                value={fmt(result.entries)}
              />
              <StatCard label={strings.statErrors} value={fmt(errors)} />
              <StatCard label={strings.statWarnings} value={fmt(warnings)} />
              <StatCard
                label={strings.statChanges}
                value={fmt(result.changes.length)}
              />
            </StatGrid>
            <p className={HINT}>
              {strings.rewrittenNote}{" "}
              {result.sorted ? strings.sortedNote : null}
            </p>
            <pre className="max-h-[28rem] overflow-auto rounded-lg border border-border bg-card p-4 font-mono text-foreground text-xs leading-relaxed">
              {result.output}
            </pre>
          </div>
        ) : null}
        {result && result.entries > 0 ? (
          <BibReport
            changeCount={tPlural(
              strings.changeCount,
              locale,
              result.changes.length,
              {
                count: fmt(result.changes.length),
              }
            )}
            changes={result.changes}
            issues={result.issues}
            lineLabel={(line) => t(strings.line, { line: fmt(line) })}
            strings={strings}
          />
        ) : null}
      </div>
    </section>
  );
}
